import { useCallback, useEffect, useState } from 'react'
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import TableLoading from 'components/TableLoading'
import {
  ProductCollectionDto,
  createProductCollection,
  deleteProductCollection,
  listProductCollections,
  updateProductCollection
} from 'api/product-collections'

const CollectionsTab = () => {
  const [rows, setRows] = useState<ProductCollectionDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<ProductCollectionDto | null>(null)
  const [nameInput, setNameInput] = useState('')
  const [displayOrderInput, setDisplayOrderInput] = useState('0')
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setError(null)
    try {
      const list = await listProductCollections()
      setRows(list)
    } catch (e: any) {
      setError(e?.message || 'Failed to load collections')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const openCreate = () => {
    setEditing(null)
    setNameInput('')
    setDisplayOrderInput('0')
    setDialogOpen(true)
  }

  const openEdit = (row: ProductCollectionDto) => {
    setEditing(row)
    setNameInput(row.name)
    setDisplayOrderInput(String(row.displayOrder ?? 0))
    setDialogOpen(true)
  }

  const handleSave = async () => {
    const n = nameInput.trim()
    if (!n) return
    const parsedDisplayOrder = Number(displayOrderInput)
    const displayOrder =
      Number.isInteger(parsedDisplayOrder) && parsedDisplayOrder >= 0
        ? parsedDisplayOrder
        : 0
    setSaving(true)
    setError(null)
    try {
      if (editing) {
        await updateProductCollection(editing.id, n, displayOrder)
      } else {
        await createProductCollection(n, displayOrder)
      }
      setDialogOpen(false)
      await load()
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (row: ProductCollectionDto) => {
    if (
      !window.confirm(
        `Delete collection "${row.name}"? Products using it must be reassigned first.`
      )
    ) {
      return
    }
    setError(null)
    try {
      await deleteProductCollection(row.id)
      await load()
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Delete failed')
    }
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 2,
          gap: 2,
          flexWrap: 'wrap'
        }}
      >
        <Typography
          variant='body2'
          color='text.secondary'
          sx={{ maxWidth: 720 }}
        >
          Collections group cabinet colors and control the order shown in the
          storefront. NetSuite product sync updates these records automatically
          when PRODUCT_LOOKUP_DATA_SYNC changes.
        </Typography>
        <Button
          variant='contained'
          startIcon={<AddIcon />}
          onClick={openCreate}
        >
          Add collection
        </Button>
      </Box>

      {error && (
        <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} variant='outlined'>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 140 }}>
                Display Order
              </TableCell>
              <TableCell align='right' sx={{ fontWeight: 600, width: 120 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3}>
                  <TableLoading />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align='center'>
                  No collections yet. Sync NetSuite products or add a
                  collection.
                </TableCell>
              </TableRow>
            ) : (
              rows.map(r => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.name}</TableCell>
                  <TableCell>{r.displayOrder ?? 0}</TableCell>
                  <TableCell align='right'>
                    <IconButton
                      size='small'
                      aria-label='edit'
                      onClick={() => openEdit(r)}
                    >
                      <EditIcon fontSize='small' />
                    </IconButton>
                    <IconButton
                      size='small'
                      aria-label='delete'
                      onClick={() => handleDelete(r)}
                    >
                      <DeleteOutlineIcon fontSize='small' />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={dialogOpen}
        onClose={() => !saving && setDialogOpen(false)}
        maxWidth='sm'
        fullWidth
        disableScrollLock
      >
        <DialogTitle>
          {editing ? 'Edit collection' : 'New collection'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin='dense'
            label='Name'
            fullWidth
            value={nameInput}
            onChange={e => setNameInput(e.target.value)}
            placeholder='e.g. Shaker Style Essential Collection'
          />
          <TextField
            margin='dense'
            label='Display Order'
            type='number'
            fullWidth
            value={displayOrderInput}
            onChange={e => setDisplayOrderInput(e.target.value)}
            inputProps={{ min: 0, step: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={handleSave}
            disabled={saving || !nameInput.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default CollectionsTab
